
import { Card, CardContent } from "@/components/ui/card";
import { Check } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { MedicationEditForm } from "./MedicationEditForm";

interface MedicationConfirmationProps {
  medication: {
    id: string;
    name: string;
    dosage: string;
    frequency?: string;
    start_date?: string;
    end_date?: string | null;
    expiry_date?: string | null;
    notes?: string | null;
    reminder_time?: string[];
  } | null;
}

export const MedicationConfirmation = ({ medication }: MedicationConfirmationProps) => {
  const [showEditForm, setShowEditForm] = useState(false);
  
  // Date actuelle formatée pour l'affichage
  const today = new Date();
  const formattedDate = format(today, "dd.MM.yyyy", { locale: fr });
  const formattedTime = format(today, "HH:mm", { locale: fr });
  
  // Nom du médicament (utilise les données réelles ou valeur par défaut)
  const medicationName = medication?.name || "Atorvastatine 20mg";
  const dosage = medication?.dosage || "1 Unité(s)";

  const handleEditClick = () => {
    if (medication) {
      setShowEditForm(true);
    } else {
      console.log("Aucun médicament à modifier");
    }
  };

  return (
    <Card className="overflow-hidden">
      <div className="bg-blue-500 text-white p-4 text-center">
        <h2 className="text-xl font-medium">Super, vous avez tout fini !</h2>
        <Button 
          variant="secondary" 
          className="mt-2 bg-white/30 hover:bg-white/40 text-white rounded-full"
        >
          Afficher la photo du jour
        </Button>
      </div>
      
      <CardContent className="p-6">
        <div className="flex justify-center items-center p-6">
          <div className="relative">
            {/* Cercle extérieur avec les points */}
            <div className="w-64 h-64 rounded-full border-4 border-dotted border-gray-200 flex items-center justify-center">
              {/* Cercle principal */}
              <div className="w-56 h-56 rounded-full border-8 border-green-500 flex flex-col items-center justify-center">
                <div className="text-green-500 font-medium text-center space-y-1">
                  <div className="text-2xl">Bien</div>
                  <div className="text-2xl">pris</div>
                  
                  {/* Cercle avec icône de vérification */}
                  <div className="flex justify-center my-2">
                    <div className="w-16 h-16 rounded-full bg-green-500 flex items-center justify-center">
                      <Check className="w-8 h-8 text-white" />
                    </div>
                  </div>
                  
                  <div className="text-sm">
                    le {formattedDate} à
                  </div>
                  <div className="text-2xl text-green-500 font-bold">
                    {formattedTime}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="border rounded-lg p-4 mt-2">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-indigo-700 rounded-full flex items-center justify-center mr-3">
              <div className="text-white text-xs">+</div>
            </div>
            <div>
              <h3 className="text-indigo-700 font-medium text-lg">{medicationName}</h3>
              <p className="text-indigo-600 opacity-70">{dosage}</p>
            </div>
          </div>
        </div>
        
        <Button 
          className="w-full mt-4 bg-indigo-700 hover:bg-indigo-800 text-white"
          variant="default"
          onClick={handleEditClick}
        >
          Modifier l'entrée
        </Button>
      </CardContent>

      {medication && (
        <MedicationEditForm 
          medication={medication}
          open={showEditForm}
          onOpenChange={setShowEditForm}
        />
      )}
    </Card>
  );
};
